/**
 * O histórico do caso (E137.F3, GUS-160, ADR-0093): a verificação da trilha e os registros em
 * ordem, cada um com o rótulo neutro do fato, quando, quem (ou a plataforma) e o que o registro
 * guarda. Da equipe que atende o caso.
 */
import type { AxiosInstance } from 'axios';

import { Badge } from '@/components/ui';
import { CartaoDaAbertura } from '@/components/Abertura/PecasDaAbertura';
import { ptBR } from '@/i18n/pt-BR';
import type {
  EventoNoHistoricoData,
  HistoricoDoCasoData,
  VerificacaoDaTrilhaData,
} from '@/lib/historico_do_caso';
import { formatarDataHora } from '@/lib/trilha_format';

import { CorrigirEvento } from './CorrigirEvento';
import { linhasDoEvento } from './detalheDoEvento';

const t = ptBR.historicoDoCaso;

function Verificacao({ verificacao }: { verificacao: VerificacaoDaTrilhaData }): JSX.Element {
  const variante = { integra: 'success', violada: 'danger', vazia: 'neutral' } as const;
  const texto =
    verificacao.situacao === 'integra'
      ? t.verificacao.integra.replace('{n}', String(verificacao.eventos))
      : t.verificacao[verificacao.situacao];
  return (
    <CartaoDaAbertura titulo={t.verificacao.titulo} testId="historico-verificacao">
      <div className="flex flex-wrap items-center gap-sc-2">
        <Badge variant={variante[verificacao.situacao]} data-testid="historico-situacao">
          {texto}
        </Badge>
      </div>
      {verificacao.problemas.length > 0 ? (
        <ul className="mt-sc-2 list-disc pl-sc-4 text-body-sm text-danger" role="alert">
          {verificacao.problemas.map((p) => (
            <li key={`${p.seq}-${p.problema}`}>
              {t.verificacao.problema[p.problema].replace('{seq}', String(p.seq))}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="mt-sc-2 text-caption text-fg-muted">{t.verificacao.limite}</p>
    </CartaoDaAbertura>
  );
}

interface RegistroProps {
  client: AxiosInstance;
  caseId: string;
  evento: EventoNoHistoricoData;
  seqPorId: ReadonlyMap<string, number>;
  onCorrigido: () => Promise<void>;
}

/**
 * Quem agiu: a plataforma na rotina; senão o nome de exibição (E209.F3.T05, GUS-296 item 7b: a conta
 * do sistema saía pelo e-mail de exemplo), com o e-mail na dica.
 */
export function quemAgiu(evento: EventoNoHistoricoData): {
  nome: string;
  dica: string | undefined;
} {
  if (evento.ator === 'rotina') return { nome: t.plataforma, dica: undefined };
  const nome = evento.quem_nome?.trim() || evento.quem || t.plataforma;
  return { nome, dica: evento.quem !== null && evento.quem !== nome ? evento.quem : undefined };
}

function Registro({ client, caseId, evento, seqPorId, onCorrigido }: RegistroProps): JSX.Element {
  const quem = quemAgiu(evento);
  return (
    <li
      className="border-b border-border py-sc-3 last:border-b-0"
      data-testid={`historico-evento-${evento.seq}`}
    >
      <div className="flex flex-wrap items-baseline gap-sc-2">
        <span className="text-caption text-fg-dim">
          {t.registro.replace('{seq}', String(evento.seq))}
        </span>
        <span className="text-body-sm font-semibold text-fg">{t.tipo[evento.tipo]}</span>
      </div>
      <p className="text-caption text-fg-muted">
        {`${formatarDataHora(evento.instante)} · `}
        <span title={quem.dica} data-testid={`historico-evento-${evento.seq}-quem`}>
          {quem.nome}
        </span>
      </p>
      {linhasDoEvento(evento, seqPorId).map((linha) => (
        <p key={linha} className="text-body-sm text-fg">
          {linha}
        </p>
      ))}
      <CorrigirEvento
        client={client}
        caseId={caseId}
        eventoId={evento.id}
        onCorrigido={onCorrigido}
      />
    </li>
  );
}

export interface HistoricoDoCasoProps {
  client: AxiosInstance;
  caseId: string;
  historico: HistoricoDoCasoData;
  onCorrigido: () => Promise<void>;
}

export function HistoricoDoCaso({
  client,
  caseId,
  historico,
  onCorrigido,
}: HistoricoDoCasoProps): JSX.Element {
  const seqPorId = new Map(historico.eventos.map((e) => [e.id, e.seq]));
  return (
    <div className="flex flex-col gap-sc-6" data-testid="historico-do-caso">
      <Verificacao verificacao={historico.verificacao} />
      {historico.eventos.length === 0 ? (
        <p className="text-body-sm text-fg-muted" data-testid="historico-vazio">
          {t.vazio}
        </p>
      ) : (
        <ol className="flex flex-col">
          {historico.eventos.map((evento) => (
            <Registro
              key={evento.id}
              client={client}
              caseId={caseId}
              evento={evento}
              seqPorId={seqPorId}
              onCorrigido={onCorrigido}
            />
          ))}
        </ol>
      )}
    </div>
  );
}
